import {
	FileExtension,
	fileExtensionSchema,
	IsEncrypted,
	MAX_UPLOAD_FILE_SIZE_BYTES,
	MAX_UPLOAD_FILE_SIZE_MB,
} from "@inrev/common";
import { useCallback, useState } from "react";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import { HiOutlineDocumentPlus } from "react-icons/hi2";
import { getMimeTypes } from "../../utils/mime";
import { logDataToSentry } from "../../utils/sentry";
import { getUploadErrorMessage, UploadErrorCode } from "../../utils/upload-error-messages";
import { Modal } from "../layout/Modal";
import { cn } from "../lib/utils";
import { Text } from "../primitives";
import { trackingAttrs } from "../utils";
import { Button } from "./Button";

type DropzoneProps = {
	allowedExtensions?: FileExtension[];
	onSuccess: (file: File[]) => void;
	className?: string;
	disabled?: boolean;
	condensed?: boolean;
	placeholderText?: string;
	trackingId?: string;
};

export const MAX_FILE_SIZE_MB = MAX_UPLOAD_FILE_SIZE_MB;

const sizeValidator = (file: File) => {
	if (file.size === 0) {
		return {
			code: "no-file-size",
			message: getUploadErrorMessage(UploadErrorCode.fileTooSmall).description,
		};
	}
	return null;
};

export const parseFileExt = (file: File) => {
	const match = file.name.match(/\.([a-zA-Z0-9]+)$/);
	const ext = match ? match[1] : null;
	const extResult = fileExtensionSchema.safeParse(ext);
	if (extResult.error) {
		return extResult.error;
	}
	return extResult.data;
};

export const encryptionValidator = async (file: File) => {
	const ext = parseFileExt(file);
	if (typeof ext !== "string") {
		return { error: DropzoneErrorCode.FileInvalidType, file };
	}
	if (await IsEncrypted.isFileEncrypted(file, ext)) {
		return { error: DropzoneErrorCode.FileIsEncrypted, file };
	}
	return;
};

export enum DropzoneErrorCode {
	FileIsEncrypted = "file-is-encrypted",
	FileInvalidType = ErrorCode.FileInvalidType,
	FileTooLarge = ErrorCode.FileTooLarge,
	FileTooSmall = ErrorCode.FileTooSmall,
	TooManyFiles = ErrorCode.TooManyFiles,
	UnknownFileError = "unknown-error",
}

type DropzoneErrorKey = `${DropzoneErrorCode}`;

type DropzoneInnerModalProps = Pick<DropzoneProps, "allowedExtensions"> & {
	dropError: DropzoneErrorCode;
	onClose?: () => void;
};

type DropzoneErrorFormat = ((props: DropzoneInnerModalProps) => string) | string;

type DropzoneErrorFormatters = {
	header: string;
	description: DropzoneErrorFormat;
};

export const DROPZONE_ERROR_FORMATTERS: Record<DropzoneErrorKey, DropzoneErrorFormatters> = {
	"too-many-files": getUploadErrorMessage(UploadErrorCode.tooManyFiles),
	"file-invalid-type": {
		header: getUploadErrorMessage(UploadErrorCode.fileInvalidType).header,
		description: ({ allowedExtensions }) =>
			getUploadErrorMessage(UploadErrorCode.fileInvalidType, { allowedExtensions }).description,
	},
	"file-too-large": getUploadErrorMessage(UploadErrorCode.fileTooLarge),
	"file-is-encrypted": getUploadErrorMessage(UploadErrorCode.fileIsEncrypted),
	"unknown-error": getUploadErrorMessage(UploadErrorCode.unknown),
	"file-too-small": getUploadErrorMessage(UploadErrorCode.fileTooSmall),
};

export const DropzoneInnerModal = ({
	allowedExtensions,
	dropError,
	onClose,
}: DropzoneInnerModalProps) => {
	const { header, description } = DROPZONE_ERROR_FORMATTERS[dropError];

	return (
		<Modal onClickOutside={onClose}>
			<div className="pt-[30px] pb-[35px] px-[35px] bg-white flex flex-col justify-center rounded-md shadow-lg space-y-[35px]">
				<div className="flex w-full flex-col items-center space-y-[15px]">
					<Text type="heading" color="primary" className="w-full">
						{header}
					</Text>
					<Text type="body" color="secondary">
						{typeof description === "string"
							? description
							: description({ allowedExtensions, dropError })}
					</Text>
				</div>
				<Button color="secondary" className="w-[125px]" filled onClick={onClose}>
					OK
				</Button>
			</div>
		</Modal>
	);
};

export const Dropzone = ({
	allowedExtensions,
	onSuccess,
	className,
	disabled,
	condensed = false,
	placeholderText = "Upload Documents",
	trackingId,
}: DropzoneProps) => {
	const [dropError, setDropError] = useState<DropzoneErrorCode | undefined>();

	const handleDrop = async (acceptedFiles: File[], rejections: FileRejection[]) => {
		let invalidFileType = false;
		let fileTooLarge = false;
		let fileTooSmall = false;
		let unknownRejection = false;

		const logInfo: { fileName: string; mimeType: string }[] = [];
		rejections.forEach((rejection) =>
			rejection.errors.forEach((error) => {
				if (error.code === ErrorCode.FileInvalidType) {
					invalidFileType = true;
					logInfo.push({
						fileName: rejection.file.name,
						mimeType: rejection.file.type,
					});
				} else if (error.code === ErrorCode.FileTooLarge) fileTooLarge = true;
				else if (error.code === "no-file-size") fileTooSmall = true;
				else unknownRejection = true;
			}),
		);

		if (logInfo.length > 0) {
			logDataToSentry("Invalid file type", logInfo);
		}

		if (invalidFileType) {
			setDropError(DropzoneErrorCode.FileInvalidType);
		} else if (fileTooLarge) {
			setDropError(DropzoneErrorCode.FileTooLarge);
		} else if (fileTooSmall) {
			setDropError(DropzoneErrorCode.FileTooSmall);
		} else if (unknownRejection) {
			setDropError(DropzoneErrorCode.UnknownFileError);
		} else {
			onSuccess(acceptedFiles);
		}
	};
	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: handleDrop,
		maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
		accept: getMimeTypes(allowedExtensions),
		validator: sizeValidator,
		disabled,
	});

	const clearError = useCallback((event?: React.MouseEvent<Element, MouseEvent>) => {
		event?.stopPropagation();
		setDropError(undefined);
	}, []);

	return (
		<>
			{/* NOTE: The background and border colors here intentionally match patterns/dropzone/Dropzone.tsx
			    (native Tailwind grays). Keep the two in sync until both are migrated to semantic tokens together. */}
			<div
				{...getRootProps()}
				{...trackingAttrs(trackingId)}
				className={cn(
					"w-full h-[85px] flex items-center justify-center space-x-[10px] bg-gray-50 bg-opacity-[.65] rounded-md border-dashed border-[2px] border-gray-300 text-[14px] text-secondary select-none cursor-pointer hover:bg-gray-100 hover:bg-opacity-60 relative mb-7",
					className,
					disabled
						? "opacity-50 hover:bg-gray-50 hover:bg-opacity-[.65] cursor-no-drop"
						: undefined,
					isDragActive ? "bg-gray-100 border-gray-500" : undefined,
					condensed ? "h-[46px] max-h-[46px] text-[16px]" : undefined,
				)}
			>
				<input {...getInputProps()} className="hidden" />
				{disabled ? (
					<div>File uploading is not allowed</div>
				) : condensed ? (
					<>
						<HiOutlineDocumentPlus className="text-[18px]" />
					</>
				) : (
					<>
						<HiOutlineDocumentPlus className="text-[18px]" />
						<div>{placeholderText}</div>
					</>
				)}
				{dropError && (
					<DropzoneInnerModal
						dropError={dropError}
						allowedExtensions={allowedExtensions}
						onClose={clearError}
					/>
				)}
				{!disabled && (
					<span className="absolute right-0 bottom-[-24px] flex justify-end text-[12px] text-secondary mt-1">
						Max file size: {MAX_FILE_SIZE_MB}MB
					</span>
				)}
			</div>
		</>
	);
};
